import { useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { useSEO } from "../../lib/seo";
import { Link } from "react-router-dom";

const fields = [
  { key: "pools", label: "Pools per route day", min: 1, max: 30, step: 1, def: 16 },
  { key: "rate", label: "Avg monthly rate per pool ($)", min: 50, max: 500, step: 5, def: 180 },
  { key: "chem", label: "Chemical cost per visit ($)", min: 0, max: 40, step: 1, def: 9 },
  { key: "wage", label: "Tech hourly wage ($)", min: 10, max: 60, step: 1, def: 22 },
  { key: "hours", label: "Route hours per day", min: 4, max: 12, step: 0.5, def: 9 },
  { key: "overhead", label: "Overhead per stop ($, fuel/ins/vehicle)", min: 0, max: 25, step: 0.5, def: 6 },
];

export default function RouteCalculator() {
  const [v, setV] = useState(Object.fromEntries(fields.map((f) => [f.key, f.def])));

  useSEO({
    title: "Route Profitability Calculator for Pool Service",
    description: "Free calculator: model revenue, chemical cost, labor and overhead per pool route to find your real profit margin per route day.",
  });

  const r = useMemo(() => {
    const visitsPerMonth = 4.33;
    const monthlyRevenue = v.pools * v.rate;
    const revenuePerDay = monthlyRevenue / visitsPerMonth;
    const laborPerDay = v.hours * v.wage;
    const chemPerDay = v.pools * v.chem;
    const overheadPerDay = v.pools * v.overhead;
    const costPerDay = laborPerDay + chemPerDay + overheadPerDay;
    const profitPerDay = revenuePerDay - costPerDay;
    const margin = revenuePerDay > 0 ? (profitPerDay / revenuePerDay) * 100 : 0;
    const profitPerStop = v.pools > 0 ? profitPerDay / v.pools : 0;
    return { revenuePerDay, laborPerDay, chemPerDay, overheadPerDay, costPerDay, profitPerDay, margin, profitPerStop, monthlyRevenue };
  }, [v]);

  const chartData = [
    { name: "Revenue", value: Math.round(r.revenuePerDay), color: "#0EA5E9" },
    { name: "Labor", value: Math.round(r.laborPerDay), color: "#64748B" },
    { name: "Chemicals", value: Math.round(r.chemPerDay), color: "#06B6D4" },
    { name: "Overhead", value: Math.round(r.overheadPerDay), color: "#94A3B8" },
    { name: "Profit", value: Math.round(r.profitPerDay), color: r.profitPerDay >= 0 ? "#10B981" : "#EF4444" },
  ];

  const money = (n) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16" data-testid="route-calculator-page">
      <nav className="text-xs text-slate-400 mb-6"><Link to="/tools" className="hover:text-sky-600">Tools</Link> / Route Profitability</nav>
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900">Route Profitability Calculator</h1>
      <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">Adjust the sliders to match one of your route days. Results update in real time.</p>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="bg-white rounded-2xl border border-border p-8 space-y-7">
          {fields.map((f) => (
            <div key={f.key}>
              <div className="flex justify-between items-baseline mb-2">
                <label className="text-sm font-semibold text-slate-700">{f.label}</label>
                <span className="font-heading font-bold text-sky-600" data-testid={`route-value-${f.key}`}>{v[f.key]}</span>
              </div>
              <input
                type="range"
                data-testid={`route-input-${f.key}`}
                min={f.min} max={f.max} step={f.step}
                value={v[f.key]}
                onChange={(e) => setV({ ...v, [f.key]: parseFloat(e.target.value) })}
                className="w-full accent-sky-500"
              />
            </div>
          ))}
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Profit / route day" value={money(r.profitPerDay)} highlight testId="route-result-profit" />
            <Stat label="Margin" value={`${r.margin.toFixed(1)}%`} testId="route-result-margin" />
            <Stat label="Revenue / route day" value={money(r.revenuePerDay)} testId="route-result-revenue" />
            <Stat label="Profit / stop" value={money(r.profitPerStop)} testId="route-result-perstop" />
          </div>
          <div className="bg-white rounded-2xl border border-border p-6">
            <h2 className="font-heading font-bold text-slate-900 text-sm mb-4">Daily breakdown ($)</h2>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(val) => money(val)} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {chartData.map((d) => <Cell key={d.name} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-sky-50 border border-sky-100 rounded-2xl p-6 text-sm text-slate-700 leading-relaxed">
            <strong className="font-heading">Rule of thumb:</strong> healthy residential routes run 35–50% margin per route day. Below 30%? Raise rates on your worst accounts first — see our <Link to="/guides/pool-service-pricing-guide" className="text-sky-600 underline">pricing guide</Link>.
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight, testId }) {
  return (
    <div className={`rounded-2xl border p-5 ${highlight ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-border"}`}>
      <p className={`text-xs uppercase tracking-[0.15em] font-semibold ${highlight ? "text-sky-400" : "text-slate-400"}`}>{label}</p>
      <p className={`font-heading font-black text-2xl mt-1 ${highlight ? "text-white" : "text-slate-900"}`} data-testid={testId}>{value}</p>
    </div>
  );
}
